export interface AstroInput {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  fire: boolean;
  firePressed?: boolean;
  pulse?: boolean;
  pulsePressed?: boolean;
  restart?: boolean;
}

export interface PlayerState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  rotation: number;
  lives: number;
  shield: number;
  maxShield: number;
  overdrive: number;
  maxOverdrive: number;
  fireCooldown: number;
  pulseCooldown: number;
  invulnTimer: number;
}

export type EnemyType = 'invader' | 'debris' | 'barrier' | 'commander';

export interface EnemyState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  hp: number;
  maxHp: number;
  type: EnemyType;
  isTrapped: boolean;
  stasisTimer: number;
  col?: number;
  row?: number;
}

export interface BulletState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  fromPlayer: boolean;
  isReflected: boolean;
  damage: number;
}

export interface PowerupState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  type: 'shield' | 'overdrive' | 'score';
}

export interface PulseWaveState {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  duration: number;
  elapsed: number;
}

export interface AstroEngineConfig {
  width: number;
  height: number;
  initialLives?: number;
}

export class AstroEngine {
  readonly width: number;
  readonly height: number;

  player: PlayerState;
  enemies: EnemyState[] = [];
  bullets: BulletState[] = [];
  powerups: PowerupState[] = [];
  pulses: PulseWaveState[] = [];

  score = 0;
  wave = 1;
  isGameOver = false;
  message = '';
  messageTimer = 0;

  private nextEntityId = 1;
  private waveMarchDir = 1;
  private waveMarchCd = 0;
  private waveFormationY = 100;

  constructor(config: AstroEngineConfig = { width: 800, height: 600 }) {
    this.width = config.width;
    this.height = config.height;

    this.player = {
      x: this.width / 2,
      y: this.height - 60,
      vx: 0,
      vy: 0,
      radius: 16,
      rotation: 0,
      lives: config.initialLives ?? 3,
      shield: 100,
      maxShield: 100,
      overdrive: 0,
      maxOverdrive: 100,
      fireCooldown: 0,
      pulseCooldown: 0,
      invulnTimer: 0,
    };

    this.startWave(1);
  }

  public reset(): void {
    this.score = 0;
    this.wave = 1;
    this.isGameOver = false;
    this.message = 'GRAVITY SURGE ENGAGED';
    this.messageTimer = 2.0;

    this.player.x = this.width / 2;
    this.player.y = this.height - 60;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.lives = 3;
    this.player.shield = 100;
    this.player.overdrive = 0;
    this.player.fireCooldown = 0;
    this.player.pulseCooldown = 0;
    this.player.invulnTimer = 0;

    this.enemies = [];
    this.bullets = [];
    this.powerups = [];
    this.pulses = [];

    this.startWave(1);
  }

  public startWave(waveNum: number): void {
    this.wave = waveNum;
    this.enemies = [];
    this.waveMarchDir = 1;
    this.waveFormationY = 80;

    // Spawn Space Invader style formation at top
    const cols = 8;
    const rows = Math.min(5, 2 + Math.floor(waveNum / 2));
    const startX = (this.width - cols * 50) / 2 + 25;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const type: EnemyType = r === 0 ? 'commander' : 'invader';
        this.enemies.push({
          id: this.nextEntityId++,
          x: startX + c * 50,
          y: this.waveFormationY + r * 40,
          vx: 0,
          vy: 0,
          radius: 14,
          hp: type === 'commander' ? 2 : 1,
          maxHp: type === 'commander' ? 2 : 1,
          type,
          isTrapped: false,
          stasisTimer: 0,
          col: c,
          row: r,
        });
      }
    }

    // Spawn Battle City / Asteroids destructible barriers and drifting debris
    const barrierCount = 3;
    for (let i = 0; i < barrierCount; i++) {
      const bx = (this.width / (barrierCount + 1)) * (i + 1);
      this.enemies.push({
        id: this.nextEntityId++,
        x: bx,
        y: this.height - 180,
        vx: 0,
        vy: 0,
        radius: 18,
        hp: 4,
        maxHp: 4,
        type: 'barrier',
        isTrapped: false,
        stasisTimer: 0,
      });
    }

    // Spawn drifting Asteroids debris
    const debrisCount = Math.min(6, 2 + waveNum);
    for (let i = 0; i < debrisCount; i++) {
      this.enemies.push({
        id: this.nextEntityId++,
        x: Math.random() * (this.width - 100) + 50,
        y: 200 + Math.random() * 150,
        vx: (Math.random() - 0.5) * 60,
        vy: (Math.random() - 0.5) * 40,
        radius: 16,
        hp: 2,
        maxHp: 2,
        type: 'debris',
        isTrapped: false,
        stasisTimer: 0,
      });
    }

    this.message = `WAVE ${waveNum}`;
    this.messageTimer = 1.5;
  }

  public update(dt: number, input: AstroInput, audioEnergy = 0.5): void {
    if (this.isGameOver) {
      if (input.restart) {
        this.reset();
      }
      return;
    }

    if (this.messageTimer > 0) {
      this.messageTimer -= dt;
    }

    this.player.fireCooldown = Math.max(0, this.player.fireCooldown - dt);
    this.player.pulseCooldown = Math.max(0, this.player.pulseCooldown - dt);
    this.player.invulnTimer = Math.max(0, this.player.invulnTimer - dt);

    // Player Movement
    const speed = 280 + audioEnergy * 60;
    if (input.left) this.player.x -= speed * dt;
    if (input.right) this.player.x += speed * dt;
    if (input.up) this.player.y -= speed * dt;
    if (input.down) this.player.y += speed * dt;

    // Constrain player to screen
    this.player.x = Math.max(this.player.radius, Math.min(this.width - this.player.radius, this.player.x));
    this.player.y = Math.max(this.height / 2, Math.min(this.height - this.player.radius - 10, this.player.y));

    // Player Fire
    if (input.fire && this.player.fireCooldown <= 0) {
      this.player.fireCooldown = 0.18;
      this.bullets.push({
        id: this.nextEntityId++,
        x: this.player.x,
        y: this.player.y - 18,
        vx: 0,
        vy: -550,
        radius: 4,
        fromPlayer: true,
        isReflected: false,
        damage: 1,
      });

      // Overdrive dual shot if gauge full
      if (this.player.overdrive >= this.player.maxOverdrive) {
        this.bullets.push({
          id: this.nextEntityId++,
          x: this.player.x - 12,
          y: this.player.y - 12,
          vx: -60,
          vy: -520,
          radius: 5,
          fromPlayer: true,
          isReflected: false,
          damage: 1,
        });
        this.bullets.push({
          id: this.nextEntityId++,
          x: this.player.x + 12,
          y: this.player.y - 12,
          vx: 60,
          vy: -520,
          radius: 5,
          fromPlayer: true,
          isReflected: false,
          damage: 1,
        });
      }
    }

    // Trigger Gravity Pulse (Repulsion Field)
    const triggerPulse = input.pulsePressed || (input.pulse && this.player.pulseCooldown <= 0);
    if (triggerPulse && this.player.pulseCooldown <= 0) {
      this.player.pulseCooldown = 0.8;
      const pulseRadius = 140;
      this.pulses.push({
        x: this.player.x,
        y: this.player.y,
        radius: 10,
        maxRadius: pulseRadius,
        duration: 0.35,
        elapsed: 0,
      });

      // Apply Repulsion Force & Stasis Detonation
      for (const b of this.bullets) {
        if (!b.fromPlayer) {
          const dx = b.x - this.player.x;
          const dy = b.y - this.player.y;
          const dist = Math.hypot(dx, dy);
          if (dist < pulseRadius) {
            b.fromPlayer = true;
            b.isReflected = true;
            b.vy = -Math.abs(b.vy) * 1.3;
            b.vx = (dx / (dist || 1)) * 200;
            this.score += 15;
          }
        }
      }

      // Detonate trapped enemies or repulse debris
      for (const e of this.enemies) {
        const dx = e.x - this.player.x;
        const dy = e.y - this.player.y;
        const dist = Math.hypot(dx, dy);
        if (dist < pulseRadius) {
          if (e.isTrapped) {
            e.hp = 0; // Detonate trapped enemy
            this.score += 100 * this.wave;
            this.player.overdrive = Math.min(this.player.maxOverdrive, this.player.overdrive + 20);
          } else if (e.type === 'debris') {
            e.vx = (dx / (dist || 1)) * 300;
            e.vy = (dy / (dist || 1)) * 300;
          }
        }
      }
    }

    // Update Pulses
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const p = this.pulses[i];
      p.elapsed += dt;
      p.radius = (p.elapsed / p.duration) * p.maxRadius;
      if (p.elapsed >= p.duration) {
        this.pulses.splice(i, 1);
      }
    }

    // Update Bullets
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      if (b.y < -20 || b.y > this.height + 20 || b.x < -20 || b.x > this.width + 20) {
        this.bullets.splice(i, 1);
      }
    }

    // Update Enemy Formation Marching (Space Invaders behavior)
    this.waveMarchCd -= dt;
    if (this.waveMarchCd <= 0) {
      this.waveMarchCd = Math.max(0.2, 0.7 - this.wave * 0.05 - audioEnergy * 0.2);
      const step = 15;
      let hitEdge = false;

      const formationEnemies = this.enemies.filter((e) => e.type === 'invader' || e.type === 'commander');
      for (const e of formationEnemies) {
        if (e.isTrapped) continue;
        if ((this.waveMarchDir === 1 && e.x + step > this.width - 30) || (this.waveMarchDir === -1 && e.x - step < 30)) {
          hitEdge = true;
        }
      }

      if (hitEdge) {
        this.waveMarchDir *= -1;
        for (const e of formationEnemies) {
          if (!e.isTrapped) e.y += 18;
        }
      } else {
        for (const e of formationEnemies) {
          if (!e.isTrapped) e.x += this.waveMarchDir * step;
        }
      }

      // Random Enemy Shoot
      if (formationEnemies.length > 0 && Math.random() < 0.3 + this.wave * 0.05) {
        const shooter = formationEnemies[Math.floor(Math.random() * formationEnemies.length)];
        this.bullets.push({
          id: this.nextEntityId++,
          x: shooter.x,
          y: shooter.y + 12,
          vx: (Math.random() - 0.5) * 40,
          vy: 220 + this.wave * 20,
          radius: 4,
          fromPlayer: false,
          isReflected: false,
          damage: 1,
        });
      }
    }

    // Clean up dead enemies & update remaining
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];

      if (e.hp <= 0) {
        this.enemies.splice(i, 1);
        continue;
      }

      if (e.isTrapped) {
        e.stasisTimer -= dt;
        if (e.stasisTimer <= 0) {
          e.isTrapped = false;
        }
      } else if (e.type === 'debris') {
        e.x += e.vx * dt;
        e.y += e.vy * dt;

        // Bounce off screen edges
        if (e.x - e.radius < 0 || e.x + e.radius > this.width) e.vx *= -1;
        if (e.y - e.radius < 0 || e.y + e.radius > this.height) e.vy *= -1;
      }

      // Enemy reaches player baseline
      if ((e.type === 'invader' || e.type === 'commander') && e.y >= this.player.y - 10 && !e.isTrapped) {
        this.damagePlayer(25);
      }
    }

    // Bullet vs Enemy Collision Checks
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      if (!b.fromPlayer) continue;

      for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
        const e = this.enemies[ei];
        const dist = Math.hypot(b.x - e.x, b.y - e.y);
        if (dist < b.radius + e.radius) {
          // Trap enemy in Bubble Bobble stasis or damage
          if (!e.isTrapped && (e.type === 'invader' || e.type === 'commander')) {
            e.isTrapped = true;
            e.stasisTimer = 4.0; // trapped in stasis bubble for 4 seconds
            this.score += 20;
          } else {
            e.hp -= b.damage;
            if (e.hp <= 0) {
              this.onEnemyDestroyed(e);
              this.enemies.splice(ei, 1);
            }
          }

          this.bullets.splice(bi, 1);
          break;
        }
      }
    }

    // Bullet vs Player Collision Checks
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      if (b.fromPlayer) continue;

      const dist = Math.hypot(b.x - this.player.x, b.y - this.player.y);
      if (dist < b.radius + this.player.radius) {
        this.bullets.splice(bi, 1);
        this.damagePlayer(15);
      }
    }

    // Powerup Physics & Collection
    for (let pi = this.powerups.length - 1; pi >= 0; pi--) {
      const p = this.powerups[pi];
      p.y += p.vy * dt;

      // Gravity attract pull
      const dx = this.player.x - p.x;
      const dy = this.player.y - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 150) {
        p.vx += (dx / dist) * 180 * dt;
        p.vy += (dy / dist) * 180 * dt;
        p.x += p.vx * dt;
      }

      if (dist < p.radius + this.player.radius) {
        if (p.type === 'shield') {
          this.player.shield = Math.min(this.player.maxShield, this.player.shield + 30);
        } else if (p.type === 'overdrive') {
          this.player.overdrive = Math.min(this.player.maxOverdrive, this.player.overdrive + 40);
        } else if (p.type === 'score') {
          this.score += 150;
        }
        this.powerups.splice(pi, 1);
      } else if (p.y > this.height + 30) {
        this.powerups.splice(pi, 1);
      }
    }

    // Check Wave Completion
    const activeEnemies = this.enemies.filter((e) => e.type === 'invader' || e.type === 'commander');
    if (activeEnemies.length === 0) {
      this.score += 500 * this.wave;
      this.startWave(this.wave + 1);
    }
  }

  private damagePlayer(amount: number): void {
    if (this.player.invulnTimer > 0) return;

    if (this.player.shield > 0) {
      this.player.shield = Math.max(0, this.player.shield - amount);
      if (this.player.shield === 0) {
        this.player.invulnTimer = 1.0;
      }
    } else {
      this.player.lives -= 1;
      this.player.invulnTimer = 1.5;
      this.player.shield = 50;

      if (this.player.lives <= 0) {
        this.isGameOver = true;
        this.message = 'SYSTEM OVERRIDE - GAME OVER';
      }
    }
  }

  private onEnemyDestroyed(e: EnemyState): void {
    this.score += e.type === 'commander' ? 150 : 50;
    this.player.overdrive = Math.min(this.player.maxOverdrive, this.player.overdrive + 10);

    // Chance to drop powerup
    if (Math.random() < 0.25) {
      const types: ('shield' | 'overdrive' | 'score')[] = ['shield', 'overdrive', 'score'];
      this.powerups.push({
        id: this.nextEntityId++,
        x: e.x,
        y: e.y,
        vx: 0,
        vy: 40,
        radius: 8,
        type: types[Math.floor(Math.random() * types.length)],
      });
    }
  }
}
